<!--
	Copyright (c) 2021-present, Bäumer, Afonso, Meissner GmbH
	All rights reserved.
-->
<script setup>
import { useMediaControls } from "@vueuse/core";
import { computed, onMounted, ref, watch } from "vue";

const props = defineProps({
    id: {
        type: [String, Number],
    },

    src: {
        type: String,
        required: true,
    },

    tracks: Array,

    controls: {
        type: Boolean,
        default: true,
    },

    autoplay: Boolean,
    loop: Boolean,
    muted: Boolean,
    playsinline: Boolean,
});

const aspectRatio = ref(16 / 9);
const video = ref();

const videoAttrs = computed(() => {
    return {
        controls: props.controls,
        autoplay: props.autoplay,
        loop: props.loop,
        muted: props.muted,
        playsinline: props.playsinline,
    };
});

const { playing, muted, currentTime, duration, ended } = useMediaControls(
    video,
    {
        src: props.src,
        tracks: props.tracks,
    }
);

watch(
    () => playing.value,
    (newValue) => {
        const videoClasses = video.value.closest(".section").classList;
        if (newValue && !videoClasses.contains("video-playing")) {
            videoClasses.add("video-playing");
        } else {
            videoClasses.remove("video-playing");
        }
    }
);

const getAspectRatio = ref(null);

watch(
    () => aspectRatio.value,
    (newValue) => {
        getAspectRatio.value =
            aspectRatio.value < 1
                ? "portrait"
                : aspectRatio.value > 1
                ? "landscape"
                : "square";
    },
    { immediate: true }
);

watch(
    () => props.muted,
    (newValue) => {
        muted.value = newValue;
    },
    { immediate: true }
);

onMounted(async () => {
    video.value.addEventListener(
        "loadedmetadata",
        () => {
            aspectRatio.value =
                video.value.videoWidth / video.value.videoHeight;
        },
        { once: true }
    );
});
</script>

<template>
    <div
        class="video-player"
        :class="{ [`video-player--aspect-${getAspectRatio}`]: true }"
    >
        <div class="video-player__content">
            <video
                ref="video"
                class="video-player__video"
                preload="auto"
                v-bind="videoAttrs"
            />

            <div
                v-if="!playing && currentTime == 0"
                class="video-player__poster"
                @click="playing = true"
            >
                <slot name="poster" />

                <div v-if="!autoplay" class="video-player__play-button">
                    <slot name="icon" />
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
@use "@bam/scss" as *;

.video-player {
    @include v(
        (
            poster-darken-color: hsla(v(color-dark-hsl), 0.5),
        )
    );

    &__content {
        position: relative;
        aspect-ratio: v-bind(aspectRatio);
        overflow: hidden;
    }

    &__video {
        position: absolute;
        top: 0;
        left: 0;
        z-index: 1;
        width: 100%;
        height: 100%;
        object-fit: contain;
    }

    video::-webkit-media-controls-overlay-play-button {
        display: none;
    }

    &__poster {
        position: absolute;
        top: 0;
        left: 0;
        z-index: 2;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;

        .picture-component {
            flex: 1;
        }

        @include hover {
            .video-player__play-button {
                transform: scale(1.3);
            }
        }

        &--darken {
            background-color: v(poster-darken-color);
        }
    }

    &__play-button {
        position: absolute;
        color: v(color-white);
        transition: transform v(easing-back-in-out) v(duration-default);
        cursor: pointer;

        svg {
            @include svg-auto-color;
            width: 60px;
            height: 60px;

            @include mq-from(md) {
                width: 130px;
                height: 130px;
            }
        }
    }

    &--aspect-portrait {
        .video-player__content {
            @include mq-from(sm) {
                aspect-ratio: 16/9 !important;
            }

            video {
                @include mq-from(md) {
                    width: 100% !important;
                    height: 100% !important;
                    object-fit: contain !important;
                }
            }
        }
    }
}
</style>
